// ---------------------------------------------------------------------------
// C71: AI Page Generation from Brief
// C72: Per-component A/B Testing
// C73: Page Version Control + Branching
// ---------------------------------------------------------------------------
import { useCallback, useEffect, useState } from "react";
import type { PuckDiffOp } from "@ease/page-builder";
import { apiFetch, ApiError, buildApiUrl, getStoredActiveTenantId } from "@/lib/api-client";
import { useTranslation } from "@/i18n";

interface ApiEnvelope<T> {
  data: T;
}

export interface BusinessInterviewInput {
  businessName: string;
  businessType: string;
  location: string;
  primaryOffer: string;
  idealCustomer: string;
  primaryGoal: string;
  differentiators?: string[];
  mustInclude?: string[];
}

export interface ConfirmedBusinessInput {
  source: "google_places";
  placeId: string;
  name: string;
  formattedAddress: string;
  nationalPhoneNumber?: string | null;
  websiteUri?: string | null;
  rating?: number | null;
  userRatingCount?: number | null;
  primaryTypeDisplayName?: string | null;
  regularOpeningHours?: string[];
}

export interface StarterContextInput {
  templateName: string;
  templateDescription?: string;
  starterPageTitle: string;
  starterPageKind: string;
  starterPageSummary?: string;
  sectionTypes?: string[];
}

export interface BusinessEnrichmentResult {
  found: boolean;
  business: ConfirmedBusinessInput | null;
}

export interface AiGenerationInput {
  prompt?: string;
  locale?: string;
  pageType?: string;
  siteId?: string;
  brandId?: string;
  intakeMode?: "two_question" | "guided_interview" | "freeform_prompt";
  interview?: BusinessInterviewInput;
  confirmedBusiness?: ConfirmedBusinessInput;
  starterContext?: StarterContextInput;
}

export interface PuckComponent {
  type: string;
  props?: Record<string, unknown>;
}

export interface PuckData {
  content?: PuckComponent[];
  root?: Record<string, unknown>;
  zones?: Record<string, PuckComponent[]>;
}

export interface AiCheckpointGenerationMetadata {
  componentCount?: number;
  unmappedCount?: number;
  diffNative?: boolean;
  diffCount?: number;
  conversationalDiffs?: PuckDiffOp[];
  diffSummary?: string[];
  [key: string]: unknown;
}

export interface AiCheckpoint {
  id: string;
  status: "pending" | "approved" | "rejected" | "expired";
  currentVersion: number;
  context: Record<string, unknown>;
  resultEntityId: string | null;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface AiCheckpointVersion {
  checkpointId: string;
  versionNumber: number;
  output: {
    puckData?: PuckData;
    unmappedFields?: Array<{ componentId: string; fieldName: string }>;
    bindings?: Record<string, unknown>;
  };
  promptUsed: string;
  generationMetadata: AiCheckpointGenerationMetadata | null;
  createdAt: string;
}

export interface AiCheckpointDetail extends AiCheckpoint {
  versions: AiCheckpointVersion[];
}

export interface ApprovedDraftPage {
  id: string;
  title: string;
  slug: string;
  status: string;
}

export interface ApprovedAiCheckpointResult {
  checkpoint: AiCheckpoint;
  page: ApprovedDraftPage;
  version?: {
    id: string;
  };
}

export interface AiGenerationStreamSection {
  index: number;
  totalSections: number;
  componentType: string;
  components: PuckComponent[];
  unmappedFields?: Array<{ componentId: string; fieldName: string }>;
}

export interface AiGenerationStreamHandlers {
  onStart?: (event: { totalSections: number; pageType?: string; inputMode?: string }) => void;
  onSectionStart?: (event: { index: number; totalSections: number; componentType: string }) => void;
  onSection?: (event: AiGenerationStreamSection) => void;
  onCheckpoint?: (checkpoint: AiCheckpoint) => void;
  onDone?: (event: { checkpointId: string; componentCount: number }) => void;
}

export function formatAiGenerationError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401 || error.status === 403) {
      return "AI generation is unavailable for this session. Refresh the workspace and sign in again if needed.";
    }

    if (error.status === 404) {
      return "AI generation is not available in this environment yet.";
    }

    if (error.status === 409) {
      return "This draft has already been resolved. Open the saved page if it exists, or create a new draft.";
    }

    if (error.status === 429) {
      return "AI generation is rate limited right now. Try again in a moment.";
    }

    if (error.status === 503) {
      return "AI generation is temporarily unavailable right now.";
    }

    if (error.status >= 500) {
      return "AI generation encountered a server error. Try again later.";
    }

    return error.message;
  }

  if (error instanceof DOMException && error.name === "AbortError") {
    return "AI generation timed out before the draft was ready.";
  }

  return error instanceof Error ? error.message : "AI generation failed.";
}

export function getCurrentAiCheckpointVersion(
  checkpoint: AiCheckpointDetail | null | undefined,
): AiCheckpointVersion | null {
  if (!checkpoint) {
    return null;
  }

  return checkpoint.versions.find(
    (version) => version.versionNumber === checkpoint.currentVersion,
  ) ?? null;
}

export function getAiCheckpointPuckData(
  checkpoint: AiCheckpointDetail | null | undefined,
): PuckData | null {
  return getCurrentAiCheckpointVersion(checkpoint)?.output.puckData ?? null;
}

export function getAiCheckpointSections(
  checkpoint: AiCheckpointDetail | null | undefined,
): PuckComponent[] {
  return getAiCheckpointPuckData(checkpoint)?.content ?? [];
}

export function getAiCheckpointUnmappedFields(
  checkpoint: AiCheckpointDetail | null | undefined,
) {
  return getCurrentAiCheckpointVersion(checkpoint)?.output.unmappedFields ?? [];
}

function parseSseMessage(message: string): { event: string; data: unknown } | null {
  const lines = message.split("\n");
  let event = "message";
  const dataLines: string[] = [];

  for (const line of lines) {
    if (line.startsWith("event:")) {
      event = line.slice("event:".length).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice("data:".length).trim());
    }
  }

  if (dataLines.length === 0) {
    return null;
  }

  return {
    event,
    data: JSON.parse(dataLines.join("\n")) as unknown,
  };
}

export async function streamAiPageGeneration(
  input: AiGenerationInput,
  handlers: AiGenerationStreamHandlers = {},
): Promise<AiCheckpoint> {
  const path = "/crm/pages/generate/checkpoints/stream";
  const activeTenantId = getStoredActiveTenantId();
  const response = await fetch(buildApiUrl(path), {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(activeTenantId ? { "X-Tenant-Id": activeTenantId } : {}),
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: "Request failed" })) as { error?: string; code?: string };
    throw new ApiError(response.status, body.error ?? "Request failed", path, body.code);
  }

  if (!response.body) {
    throw new Error("AI generation stream did not return a response body.");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let checkpoint: AiCheckpoint | null = null;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const messages = buffer.split("\n\n");
    buffer = messages.pop() ?? "";

    for (const rawMessage of messages) {
      if (!rawMessage.trim()) {
        continue;
      }

      const parsed = parseSseMessage(rawMessage);
      if (!parsed) {
        continue;
      }

      if (parsed.event === "error") {
        const errorData = parsed.data as { message?: string; error?: string };
        throw new Error(errorData.message ?? errorData.error ?? "AI generation stream failed.");
      }

      if (parsed.event === "start") {
        handlers.onStart?.(parsed.data as { totalSections: number; pageType?: string; inputMode?: string });
      } else if (parsed.event === "section_start") {
        handlers.onSectionStart?.(parsed.data as { index: number; totalSections: number; componentType: string });
      } else if (parsed.event === "section") {
        handlers.onSection?.(parsed.data as AiGenerationStreamSection);
      } else if (parsed.event === "checkpoint") {
        checkpoint = parsed.data as AiCheckpoint;
        handlers.onCheckpoint?.(checkpoint);
      } else if (parsed.event === "done") {
        handlers.onDone?.(parsed.data as { checkpointId: string; componentCount: number });
      }
    }
  }

  if (!checkpoint) {
    throw new Error("AI generation stream finished without a checkpoint.");
  }

  return checkpoint;
}

// C71: AI generation
export function useAiPageGeneration() {
  const { locale } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AiCheckpoint | null>(null);

  const mutate = useCallback(async (
    input: AiGenerationInput,
  ) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await apiFetch<ApiEnvelope<AiCheckpoint>>("/crm/pages/generate/checkpoints", {
        method: "POST",
        body: JSON.stringify({
          ...input,
          locale: input.locale ?? locale,
        }),
      });
      setData(result.data);
      return result.data;
    } catch (caughtError) {
      setData(null);
      setError(formatAiGenerationError(caughtError));
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [locale]);

  return {
    data,
    error,
    isLoading,
    mutate,
  };
}

export function useBusinessEnrichment() {
  const [data, setData] = useState<BusinessEnrichmentResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lookup = useCallback(async (input: {
    businessName: string;
    location?: string;
    businessType?: string;
  }) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await apiFetch<ApiEnvelope<BusinessEnrichmentResult>>("/crm/pages/generate/business-enrichment", {
        method: "POST",
        body: JSON.stringify(input),
      });
      setData(result.data);
      return result.data;
    } catch (lookupError) {
      const message = lookupError instanceof ApiError
        ? lookupError.message
        : lookupError instanceof Error ? lookupError.message : "Business enrichment lookup failed.";
      setError(message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    data,
    isLoading,
    error,
    lookup,
  };
}

export async function getAiCheckpoint(checkpointId: string): Promise<AiCheckpointDetail> {
  const res = await apiFetch<ApiEnvelope<AiCheckpointDetail>>(`/crm/pages/generate/checkpoints/${checkpointId}`);
  return res.data;
}

export async function modifyAiCheckpoint(
  checkpointId: string,
  instructions: string,
  input?: { selectedComponentId?: string | null },
): Promise<AiCheckpointVersion> {
  const res = await apiFetch<ApiEnvelope<AiCheckpointVersion>>(`/crm/pages/generate/checkpoints/${checkpointId}/modify`, {
    method: "POST",
    body: JSON.stringify({
      instructions,
      ...(input?.selectedComponentId ? { selectedComponentId: input.selectedComponentId } : {}),
    }),
  });
  return res.data;
}

export async function revertAiCheckpoint(
  checkpointId: string,
  toVersion: number,
): Promise<AiCheckpoint> {
  const res = await apiFetch<ApiEnvelope<AiCheckpoint>>(`/crm/pages/generate/checkpoints/${checkpointId}/revert`, {
    method: "POST",
    body: JSON.stringify({ toVersion }),
  });
  return res.data;
}

export async function approveAiCheckpoint(
  checkpointId: string,
  input: { title: string; slug: string; pageType?: string; siteId?: string; folderId?: string },
): Promise<ApprovedAiCheckpointResult> {
  const res = await apiFetch<ApiEnvelope<ApprovedAiCheckpointResult>>(
    `/crm/pages/generate/checkpoints/${checkpointId}/approve`,
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
  return res.data;
}

export async function rejectAiCheckpoint(checkpointId: string): Promise<AiCheckpoint> {
  const res = await apiFetch<ApiEnvelope<AiCheckpoint>>(`/crm/pages/generate/checkpoints/${checkpointId}/reject`, {
    method: "POST",
  });
  return res.data;
}

// C72: A/B Testing
export interface AbVariant {
  id: string;
  componentId: string;
  name: string;
  props: Record<string, unknown>;
  trafficPercent: number;
  views: number;
  clicks: number;
  conversions: number;
}

export interface AbTest {
  id: string;
  pageId: string;
  componentId: string;
  status: "draft" | "running" | "completed";
  variants: AbVariant[];
  winnerId?: string;
  significance: number;
  startedAt?: string;
  completedAt?: string;
}

export function useAbTests(pageId: string | undefined) {
  const [tests, setTests] = useState<AbTest[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!pageId) return;
    setIsLoading(true);
    apiFetch<{ data: AbTest[] }>(`/crm/pages/${pageId}/experiments`)
      .then((res) => setTests(res.data ?? []))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [pageId]);

  return { tests, isLoading };
}

export function useCreateAbTest() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AbTest | null>(null);

  const mutate = useCallback(async (
    input: { pageId: string; name?: string; componentId: string; variants: Array<{ name: string; props: Record<string, unknown>; trafficPercent: number }> },
  ): Promise<AbTest | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await apiFetch<{ data: AbTest }>(`/crm/pages/${input.pageId}/experiments`, {
        method: "POST",
        body: JSON.stringify({
          name: input.name ?? "Untitled experiment",
          componentId: input.componentId,
          variants: input.variants,
        }),
      });
      setData(result.data);
      return result.data;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create experiment";
      setError(message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { mutate, isLoading, error, data };
}

// C73: Version Control
export interface PageVersion {
  id: string;
  pageId: string;
  version: number;
  content: unknown;
  createdBy: string;
  createdAt: string;
  isBranch: boolean;
  branchName?: string;
}

export function usePageVersions(pageId: string | undefined) {
  const [versions, setVersions] = useState<PageVersion[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!pageId) return;
    setIsLoading(true);
    apiFetch<{ data: PageVersion[] }>(`/crm/pages/${pageId}/versions`)
      .then((res) => setVersions(res.data ?? []))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [pageId]);

  return { versions, isLoading };
}

export async function restorePageVersion(pageId: string, versionId: string): Promise<void> {
  await apiFetch(`/crm/pages/${pageId}/restore/${versionId}`, { method: "POST" });
}

export async function createPageBranch(pageId: string, branchName: string): Promise<PageVersion> {
  void pageId;
  void branchName;
  throw new Error("Page branches are not mounted in the production page-builder API yet.");
}

export async function publishBranch(pageId: string, branchId: string): Promise<void> {
  void pageId;
  void branchId;
  throw new Error("Page branch publishing is not mounted in the production page-builder API yet.");
}
